/**
 * Algebra I · Level 2 — item prose, English.
 *
 * Prose for a course pack lives beside the shipped item bundles, under
 * content/lang, and never inside src/. Same rule, same reason: src/ carries no
 * language. The pack in src/content/packs imports this file and the registry
 * merges it in behind content/lang/items.*.js.
 *
 * HOUSE STYLE. Simplified technical English: one word means one thing, one
 * instruction per sentence, active voice, under twenty words. A situation is
 * ONE sentence, and it never states a number the mathematics does not use.
 */
export default {
  // -------------------------------------------------------------- Level 2 · 1
  'l2.ctx.twoHolds': 'Each hold of this freighter carries the same mass.',
  'l2.ctx.twoBays': 'Two bays on this rig are packed to the same count.',
  'l2.ask.holdMass': 'Find the solution for the variable ${v}$ in this equation.',
  'l2.ctx.gauge': 'This gauge follows a rule that nobody wrote down.',
  'l2.ask.missingWatch': 'What was the missing reading?',
  'l2.why.openBothBrackets': 'Open both brackets first. Each side is one quantity until you do.',
  'l2.why.minusEntersEveryTerm': 'The minus sign belongs to the whole bracket, so it enters every term inside it.',
  'l2.why.multiplyBothByBottom': 'Multiply both sides by {k}. This move takes the bar away.',
  'l2.why.stepTellsRate': 'Each step of the input moves the output by {n}. The rate is {n}.',
  'l2.why.applyRateOnce': 'Apply the rate to the row above the gap.',
  'l2.why.stepBackToInput': 'The output moved by one rate, so the input moved by one step.',

  // ------------------------------------------------------ inequalities · ask
  'l2.ask.whichStatement': 'An inequality leans. Which one covers every ${v}$ that works?',
  'l2.ask.whichFirstLine': 'Which line is the correct first move on this equation?',
  'l2.ask.whichBand': 'The constraint sets a limit on the inputs — the domain. It gives back a range of readings. Which band holds every ${v}$?',
  'l2.ask.leastWhole': 'What is the smallest whole ${v}$ this inequality allows?',
  'l2.ask.mostWhole': 'What is the largest whole ${v}$ this inequality allows?',
  'l2.ask.howManyWhole': 'How many whole values of ${v}$ work?',

  // ----------------------------------------------------- inequalities · sites
  'l2.ctx.hoist': 'A cargo hoist refuses any load that breaks the reading below.',
  'l2.ask.setHoist': 'Which statement gives every ${v}$ it will lift?',
  'l2.ask.leastHoist': 'What is the smallest whole ${v}$ it will lift?',
  'l2.ask.mostHoist': 'What is the largest whole ${v}$ it will lift?',
  'l2.ctx.airlock': 'An airlock cycles only while the reading below holds.',
  'l2.ask.setAirlock': 'Which statement gives every ${v}$ that opens it?',
  'l2.ask.leastAirlock': 'What is the smallest whole ${v}$ that opens it?',
  'l2.ask.mostAirlock': 'What is the largest whole ${v}$ that opens it?',
  'l2.ctx.kiln': 'A kiln keeps its glaze only while the reading holds.',
  'l2.ask.setKiln': 'Which statement gives every ${v}$ that keeps the glaze?',
  'l2.ask.leastKiln': 'What is the smallest whole ${v}$ that keeps the glaze?',
  'l2.ask.mostKiln': 'What is the largest whole ${v}$ that keeps the glaze?',
  'l2.ctx.tether': 'A tether parts the moment the reading below fails.',
  'l2.ask.setTether': 'Which statement gives every ${v}$ that is still safe?',
  'l2.ask.leastTether': 'What is the smallest whole ${v}$ that is still safe?',
  'l2.ask.mostTether': 'What is the largest whole ${v}$ that is still safe?',
  'l2.ctx.thruster': 'A thruster fires only while the reading below holds.',
  'l2.ask.setThruster': 'Which statement gives every ${v}$ that fires it?',
  'l2.ask.leastThruster': 'What is the smallest whole ${v}$ that fires it?',
  'l2.ask.mostThruster': 'What is the largest whole ${v}$ that fires it?',

  // --------------------------------------------------------------- bands
  'l2.ctx.bandGlaze': 'A glaze sets only between two temperatures, and the kiln reads one of them.',
  'l2.ctx.bandHold': 'A hold stays level only between two masses.',
  'l2.ctx.bandSeed': 'A seed tray germinates only between two light levels.',
  'l2.ctx.bandTrack': 'A rail sled runs only between two speeds.',
  'l2.ask.setBand': 'Which band of the domain and range does the constraint allow?',
  'l2.ask.countBand': 'How many whole values of ${v}$ are inside the band?',

  // ------------------------------------------------------ inequalities · why
  'l2.why.takeOffBothSides': 'Take {n} off both sides. The lean does not change.',
  'l2.why.addToBothSides': 'Add {n} to both sides. The lean does not change.',
  'l2.why.leanUnchanged': 'No move here multiplies by a negative, so this inequality still leans the same way.',
  'l2.why.leanNowRead': 'The unknown stands alone. Read the lean off it.',
  'l2.why.divideByNegativeTurns': 'Divide by {a} to turn the lean round. No other move turns it.',
  'l2.why.boundaryNotWhole': 'The boundary is not a whole number, so no whole value sits on it.',
  'l2.why.firstWholePast': 'This is the first whole number past the boundary.',
  'l2.why.lastWholeBefore': 'This is the last whole number before the boundary.',
  'l2.why.takeOffEveryPart': 'Take {n} off all three parts at once.',
  'l2.why.addToEveryPart': 'Add {n} to all three parts at once.',
  'l2.why.divideEveryPart': 'Divide all three parts by {a}. The range narrows with them.',
  'l2.why.divideByNegativeTurnsBoth': 'Dividing by {a} turns both signs round, so the band reads the other way.',
  'l2.why.bandFirst': 'Find the band the constraint allows before you count anything.',
  'l2.why.countTheEnds': 'The domain runs from one end to the other. Count both ends, not only the gap.',

  // ------------------------------------------------------ literal equations
  'l2.ask.solveFormulaFor': 'A formula written in letters is a literal equation. Rearrange it to give ${v}$ on its own.',
  'l2.ask.turnItRound': 'Give the variable ${v}$ on its own.',
  'l2.ctx.fArea': 'A hull plate is a rectangle, and this rule gives its area.',
  'l2.ctx.fDistance': 'A sled runs at a steady speed, and this rule gives the distance.',
  'l2.ctx.fForce': 'A thruster pushes a mass, and this rule gives the force.',
  'l2.ctx.fTriangle': 'A sail panel is a triangle, and this rule gives its area.',
  'l2.ctx.fPerimeter': 'A frame runs right round a rectangle, and this rule gives its length.',
  'l2.ctx.fLine': 'A straight trace climbs at a steady rate, and this rule gives its height.',
  'l2.ctx.fStandard': 'Two stores drain at fixed rates, and this rule gives the total.',
  'l2.ctx.fMean': 'This rule averages two readings and gives the middle value.',
  'l2.ctx.fTerm': 'A ladder of readings climbs by the same step, and this rule gives any rung.',
  'l2.ctx.fCone': 'An ore hopper is a cone, and this rule gives its volume.',
  'l2.ctx.fDegrees': 'This rule turns one temperature scale into the other.',
  'l2.ctx.fInterest': 'A credit balance earns at a fixed rate, and this rule gives the interest.',
  'l2.why.divideBothByLetter': 'Divide both sides by the letter standing against ${v}$.',
  'l2.why.divideBothByNumber': 'Divide both sides by the number standing against ${v}$.',
  'l2.why.clearTheBarFirst': 'Clear the fraction bar first. Nothing else can move until it is gone.',
  'l2.why.moveTheOtherTermFirst': 'Move the term that does not hold ${v}$ to the other side first.',
  'l2.why.readItRoundTheOtherWay': 'A literal equation stays true when you read it round the other way.',
  'l2.why.putBackTheOne': 'The bracket held one less than ${v}$, so add the one back.',

  // ---------------------------------------------------------------- ratios
  'l2.ask.findTheFourth': 'Two ratios that agree make a proportion. Find the missing number.',
  'l2.ask.sameRatioRow': 'Every row holds the same ratio, so the rows make a proportion. What is the missing reading?',
  'l2.ask.whichProportion': 'Which proportion states it correctly?',
  'l2.ctx.mix': 'A mixing rig keeps the same ratio of resin to hardener in every batch.',
  'l2.ask.mixHowMany': 'How much hardener does the larger batch take?',
  'l2.ctx.chart': 'On this chart, paper length and real distance keep one ratio.',
  'l2.ask.chartHowFar': 'How far is the real distance?',
  'l2.ctx.alloy': 'An alloy holds a fixed ratio of tin to copper.',
  'l2.ask.alloyHowMuch': 'How much copper does the pour take?',
  'l2.ctx.feed': 'A feed store hands out the same ratio of grain to cadets every watch.',
  'l2.ask.feedHowMany': 'How much grain does the larger watch take?',
  'l2.ctx.dye': 'A dye vat keeps the same ratio of pigment to water.',
  'l2.ask.dyeHowMuch': 'How much water does the larger vat take?',
  'l2.why.crossMultiply': 'A proportion gives one equation: multiply across the bars.',
  'l2.why.oneNumberLeft': 'One unknown is left, and it is now a plain equation.',
  'l2.why.oneRowGivesTheRate': 'One complete row gives the rate. Every other row obeys it.',
  'l2.why.applyRateToRow': 'Apply that rate to the row with the gap.',
  'l2.why.firstPairIsTheRatio': 'The first two amounts fix the ratio.',
  'l2.why.secondPairMatchesIt': 'The second pair must hold the same ratio, in the same order.',

  // ----------------------------------------------------------- slope, rate
  'l2.ask.rateBetweenReadings': 'Two readings — two points — sit on one line. What is its slope, its rate?',
  'l2.ask.rateFromTable': 'Every row obeys one line. What is its rate of change, the climb for one step?',
  'l2.ask.rateOfTrace': 'What is the slope — the rate of change — between the two points on the trace?',
  'l2.ask.ratePerStep': 'What is the rate of change per step?',
  'l2.ctx.rateWinch': 'A winch pays out cable at a steady rate, and the log holds two readings.',
  'l2.ctx.rateFrost': 'Frost creeps down the mast at a steady rate, and the log holds two readings.',
  'l2.ctx.rateTank': 'A tank fills at a steady rate, and the log holds two readings.',
  'l2.ctx.rateSled': 'A sled runs at a steady rate, and the log holds two readings.',
  'l2.ctx.rateKiln': 'A kiln heats at a steady rate, and the log holds two readings.',
  'l2.why.riseOverRun': 'The slope is the climb between the two points, divided by the step across.',
  'l2.why.oneStepDownTheTable': 'Take one step down the table and divide the climb by the step.',
  'l2.why.thatIsTheRate': 'That is the rate of change, and every pair of readings gives the same one.',
  'l2.why.oneStepIsTheRate': 'One step across gives the rate at once.',

  // ------------------------------------------------------------- the trace
  'l2.ask.drawThroughBoth': 'Draw the graph — the trace — through both readings on the coordinate plane. Its y-intercept is where it crosses the upright axis.',
  'l2.ask.drawThroughTable': 'Draw the graph, the trace, that fits every row. Its y-intercept sits where it crosses the upright axis.',
  'l2.ask.writeTheRule': 'A straight rule is a linear function: a slope, and a start that is the initial value. Which rule fits every reading?',
  'l2.ask.writeTheTrace': 'A linear function draws a straight trace from its initial value, its y-intercept. Which rule does this obey?',
  'l2.ask.traceHeightAt': 'The chart is a coordinate plane, and it already shows the trace. What is its height at $x = {val}$?',
  'l2.ask.traceReaches': 'The chart is a coordinate plane and already shows the trace. Where does it reach $y = {val}$?',
  'l2.ctx.ruleLift': 'A lift climbs at a steady rate from a landing above the ground.',
  'l2.ctx.ruleDrift': 'A buoy drifts at a steady rate from a mark off the shore.',
  'l2.ctx.ruleStack': 'A stack grows at a steady rate from a pallet already laid.',
  'l2.ctx.ruleBrine': 'A brine tank fills at a steady rate from a level already in it.',
  'l2.ask.writeRule': 'Which rule fits both readings?',
  'l2.ask.drawRule': 'Draw the graph on the coordinate plane to fit both readings.',
  'l2.ask.rateOfStatement': 'This statement draws one line through the points. What is its slope, the rate of change?',
  'l2.ask.crossesAcross': 'Where does this line cross the across axis?',
  'l2.ask.crossesUpright': 'Where does this line cross the upright axis? That reading is its y-intercept.',
  'l2.ask.sameLineOtherWay': 'Which rule draws the same line, with the same slope and y-intercept?',
  'l2.why.gatherYAlone': 'Move the $x$ term across, so only the $y$ term is left.',
  'l2.why.divideByCoefOfY': 'Divide every term by {b}, and $y$ stands alone.',
  'l2.why.otherIsZeroAcross': 'On the across axis $y$ is zero, so the $y$ term goes.',
  'l2.why.otherIsZeroUpright': 'On the upright axis $x$ is zero, so the $x$ term goes.',
  'l2.why.backToTheAxis': 'Step back across the coordinate plane to the axis, where the graph starts.',
  'l2.why.startIsTheAxis': 'The reading at zero is the y-intercept, where the graph crosses the axis.',

  // --------------------------------------------------------------- systems
  'l2.ask.findX': 'Both statements make one system. What is $x$ at their one point?',
  'l2.ask.findY': 'Both statements hold at one point. What is $y$ there?',
  'l2.ask.whichPairHoldsBoth': 'Substitute each pair into this system of equations. Which pair holds both rows?',
  'l2.ctx.pairCrates': 'In this hold, $x$ counts the light crates and $y$ counts the heavy ones.',
  'l2.ask.pairHowManyLight': 'How many light crates are there?',
  'l2.ask.pairHowManyHeavy': 'How many heavy crates are there?',
  'l2.ctx.pairTickets': 'On this deck, $x$ counts the cadet berths and $y$ counts the crew berths.',
  'l2.ask.pairHowManyCadet': 'How many cadet berths are there?',
  'l2.ask.pairHowManyCrew': 'How many crew berths are there?',
  'l2.ctx.pairAlloys': 'In this pour, $x$ counts the tin ingots and $y$ counts the lead ingots.',
  'l2.ask.pairHowManyTin': 'How many tin ingots are there?',
  'l2.ask.pairHowManyLead': 'How many lead ingots are there?',
  'l2.ctx.pairRuns': 'In this log, $x$ counts the short runs and $y$ counts the long runs.',
  'l2.ask.pairHowManyShort': 'How many short runs are there?',
  'l2.ask.pairHowManyLong': 'How many long runs are there?',
  'l2.why.putTheRuleIn': 'The first row of the system already says what $y$ is. Substitute it into the second.',
  'l2.why.oneLetterAtATime': 'One letter is gone, so what is left is an ordinary equation.',
  'l2.why.backIntoTheRule': 'Put that value back into the rule to get the other letter.',
  'l2.why.backIntoEitherStatement': 'Put that value back into either statement to get the other letter.',
  'l2.why.addTheStatements': 'Add the two rows of the system. The $y$ terms are opposite, so $y$ leaves.',
  'l2.why.scaleUntilOpposite': 'Multiply the first statement by {k}, so the two $y$ terms become opposite.',

  // ------------------------------------------------------- shared questions
  'l2.why.writeTheRuleDown': 'Write the linear function down: the slope, then the initial value.',
  'l2.ask.sameRatioAmount': 'The ratio stays the same. What is the missing amount?',
  'l2.ask.startAmount': 'Find ${v}$, the amount the run started with.',

  // --- the limit deck — one machine per world, all sharing three questions
  'l2.ctx.limCrane': 'A dock crane refuses any load that breaks the reading below.',
  'l2.ctx.limPress': 'A hull press stops when the reading below fails.',
  'l2.ctx.limPump': 'A bilge pump runs only while the reading below holds.',
  'l2.ctx.limFilter': 'An air filter lasts a whole watch only while the reading below holds.',
  'l2.ctx.limWeld': 'A weld arm holds a seam only while the reading below holds.',
  'l2.ctx.limBrake': 'A rail brake grips the track only while the reading below holds.',
  'l2.ctx.limDrone': 'A survey drone stays in the air only while the reading below holds.',
  'l2.ctx.limCoolant': 'A coolant loop keeps the core cold only while the reading below holds.',
  'l2.ctx.limDeck': 'A landing deck takes a ship only while the reading below holds.',
  'l2.ctx.limSluice': 'A sluice gate opens only while the reading below holds.',
  'l2.ctx.limForge': 'A forge keeps its heat only while the reading below holds.',
  'l2.ctx.limSpar': 'A spar carries a sail only while the reading below holds.',
  'l2.ctx.limLadder': 'A boarding ladder takes a climber only while the reading below holds.',
  'l2.ctx.limVent': 'A vent shaft stays clear only while the reading below holds.',
  'l2.ctx.limChain': 'An anchor chain holds only while the reading below holds.',
  'l2.ctx.limSolar': 'A solar wing tracks the sun only while the reading below holds.',
  'l2.ctx.limKettle': 'A brine kettle keeps its boil only while the reading below holds.',
  'l2.ctx.limScaffold': 'A scaffold stands only while the reading below holds.',
  'l2.ctx.limBeacon': 'A beacon keeps flashing only while the reading below holds.',
  'l2.ctx.limHatch': 'A cargo hatch stays sealed only while the reading below holds.',
  'l2.ctx.limMill': 'An ore mill turns only while the reading below holds.',
  'l2.ctx.limRelay': 'A relay mast answers only while the reading below holds.',
  'l2.ctx.limStill': 'A water still yields clean water only while the reading below holds.',
  'l2.ctx.limGrapple': 'A grapple line holds a hull only while the reading below holds.',

  // --- the band deck
  'l2.ctx.bandKiln2': 'A kiln fires clean only between two temperatures, and the gauge reads one.',
  'l2.ctx.bandPress': 'A press seals a plate only between two pressures.',
  'l2.ctx.bandVat': 'A dye vat takes colour only between two temperatures.',
  'l2.ctx.bandRotor': 'A rotor stays balanced only between two speeds.',
  'l2.ctx.bandTank': 'A ballast tank keeps the ship level only between two volumes.',
  'l2.ctx.bandLamp': 'A grow lamp helps the crop only between two light levels.',
  'l2.ctx.bandWeld': 'A weld holds only between two currents.',
  'l2.ctx.bandLift': 'A lift climbs safely only between two loads.',
  'l2.ctx.bandBrine': 'A brine line stays clear only between two salt levels.',
  'l2.ctx.bandRelay': 'A relay answers only between two signal strengths.',
  'l2.ctx.bandGlider': 'A glider stays up only between two air speeds.',
  'l2.ctx.bandForge': 'A forge shapes iron only between two heats.',
  'l2.ctx.bandPump': 'A pump moves water only between two pressures.',
  'l2.ctx.bandCell': 'A fuel cell gives power only between two temperatures.',
  'l2.ctx.bandNet': 'A trawl net fishes well only between two depths.',
  'l2.ctx.bandOven': 'A bread oven bakes evenly only between two temperatures.',
  'l2.ctx.bandMast': 'A mast stands safely only between two wind speeds.',
  'l2.ctx.bandFilter': 'A water filter works only between two flow rates.',
  'l2.ctx.bandCoil': 'A charge coil takes current only between two voltages.',
  'l2.ctx.bandStore': 'A cold store keeps food only between two temperatures.',

  // --- the ratio deck
  'l2.ctx.ratioMortar': 'A mortar mix keeps the same ratio of lime to sand.',
  'l2.ctx.ratioBrass': 'A brass pour keeps a fixed ratio of copper to zinc.',
  'l2.ctx.ratioInk': 'An ink press keeps the same ratio of pigment to oil.',
  'l2.ctx.ratioGlaze': 'A glaze keeps the same ratio of ash to clay.',
  'l2.ctx.ratioBrine': 'A brine keeps the same ratio of salt to water.',
  'l2.ctx.ratioSolder': 'A solder bath keeps a fixed ratio of tin to silver.',
  'l2.ctx.ratioMap': 'On this survey sheet, paper length and ground distance keep one ratio.',
  'l2.ctx.ratioModel': 'On this hull model, model length and real length keep one ratio.',
  'l2.ctx.ratioFeedMix': 'A feed mix keeps the same ratio of oats to barley.',
  'l2.ctx.ratioFuelMix': 'A fuel mix keeps the same ratio of spirit to oil.',
  'l2.ctx.ratioConcrete': 'A concrete mix keeps the same ratio of cement to gravel.',
  'l2.ctx.ratioTea': 'A galley urn keeps the same ratio of leaf to water.',
  'l2.ctx.ratioCrew': 'A watch roster keeps the same ratio of cadets to officers.',
  'l2.ctx.ratioSoil': 'A potting mix keeps the same ratio of peat to grit.',
  'l2.ctx.ratioPaint': 'A paint batch keeps the same ratio of colour to base.',

  // --- the modelling deck — the amounts live in the sentence, because the card shows nothing else
  'l2.ctx.modelResin': 'A mix uses ${a}$ resin for every ${b}$ hardener. The next uses ${e}$ hardener.',
  'l2.ctx.modelLime': 'A mortar uses ${a}$ lime for every ${b}$ sand. The next uses ${e}$ sand.',
  'l2.ctx.modelCopper': 'A pour uses ${a}$ tin for every ${b}$ copper. The next uses ${e}$ copper.',
  'l2.ctx.modelPigment': 'A vat uses ${a}$ pigment for every ${b}$ water. The next uses ${e}$ water.',
  'l2.ctx.modelGrain': 'A store gives ${a}$ grain for every ${b}$ cadets. The next watch has ${e}$ cadets.',
  'l2.ctx.modelCement': 'A batch uses ${a}$ cement for every ${b}$ gravel. The next uses ${e}$ gravel.',
  'l2.ctx.modelSalt': 'A brine uses ${a}$ salt for every ${b}$ water. The next uses ${e}$ water.',
  'l2.ctx.modelOats': 'A feed uses ${a}$ oats for every ${b}$ barley. The next uses ${e}$ barley.',
  'l2.ctx.modelSpirit': 'A fuel uses ${a}$ spirit for every ${b}$ oil. The next uses ${e}$ oil.',
  'l2.ctx.modelAsh': 'A glaze uses ${a}$ ash for every ${b}$ clay. The next uses ${e}$ clay.',
  'l2.ctx.modelPeat': 'A soil mix uses ${a}$ peat for every ${b}$ grit. The next uses ${e}$ grit.',
  'l2.ctx.modelSilver': 'A bath uses ${a}$ silver for every ${b}$ tin. The next uses ${e}$ tin.',
  'l2.ctx.modelLeaf': 'An urn uses ${a}$ leaf for every ${b}$ water. The next uses ${e}$ water.',
  'l2.ctx.modelColour': 'A batch uses ${a}$ colour for every ${b}$ base. The next uses ${e}$ base.',
  'l2.ctx.modelCadet': 'A roster keeps ${a}$ cadets for every ${b}$ officers. The next keeps ${e}$ officers.',
  'l2.ctx.modelWax': 'A pot uses ${a}$ wax for every ${b}$ wick. The next uses ${e}$ wick.',

  // --- the rate deck
  'l2.ctx.rateLift': 'A lift climbs at a steady rate, and the log holds two readings.',
  'l2.ctx.rateDrill': 'A drill sinks at a steady rate, and the log holds two readings.',
  'l2.ctx.rateSnow': 'Snow piles up at a steady rate, and the log holds two readings.',
  'l2.ctx.rateStore': 'A store empties at a steady rate, and the log holds two readings.',
  'l2.ctx.rateBuoy': 'A buoy drifts at a steady rate, and the log holds two readings.',
  'l2.ctx.rateCharge': 'A battery charges at a steady rate, and the log holds two readings.',
  'l2.ctx.rateTide': 'A tide falls at a steady rate, and the log holds two readings.',
  'l2.ctx.rateSpool': 'A spool unwinds at a steady rate, and the log holds two readings.',
  'l2.ctx.rateGlacier': 'A glacier creeps at a steady rate, and the log holds two readings.',
  'l2.ctx.rateSilo': 'A silo fills at a steady rate, and the log holds two readings.',
  'l2.ctx.rateCandle': 'A candle burns down at a steady rate, and the log holds two readings.',
  'l2.ctx.rateRust': 'Rust spreads at a steady rate, and the log holds two readings.',
  'l2.ctx.rateBelt': 'A belt carries ore at a steady rate, and the log holds two readings.',
  'l2.ctx.rateSap': 'Sap runs at a steady rate, and the log holds two readings.',
  'l2.ctx.rateDust': 'Dust settles at a steady rate, and the log holds two readings.',
  'l2.ctx.rateWell': 'A well drops at a steady rate, and the log holds two readings.',
  'l2.ctx.rateCable': 'A cable ices up at a steady rate, and the log holds two readings.',
  'l2.ctx.rateSaw': 'A saw cuts at a steady rate, and the log holds two readings.',
  'l2.ctx.rateVault': 'A seed vault fills at a steady rate, and the log holds two readings.',
  'l2.ctx.rateWax': 'A wax block melts at a steady rate, and the log holds two readings.',

  // --- the rule deck — a steady climb from a start that is not zero
  'l2.ctx.ruleCrane': 'A crane hook rises at a steady rate from a height already set.',
  'l2.ctx.ruleSilo': 'A silo fills at a steady rate from a level already in it.',
  'l2.ctx.ruleDrill': 'A drill sinks at a steady rate from a depth already cut.',
  'l2.ctx.ruleCredit': 'A credit line grows at a steady rate from a balance already held.',
  'l2.ctx.ruleReef': 'A reef grows at a steady rate from a bank already there.',
  'l2.ctx.ruleSnowline': 'A snow line climbs at a steady rate from a mark already cut.',
  'l2.ctx.ruleSpoil': 'A spoil heap grows at a steady rate from a pile already tipped.',
  'l2.ctx.ruleSail': 'A sail lets out at a steady rate from a length already run.',
  'l2.ctx.ruleWard': 'A ward count rises at a steady rate from a number already in.',
  'l2.ctx.ruleTrench': 'A trench lengthens at a steady rate from a run already dug.',
  'l2.ctx.ruleKilnRule': 'A kiln heats at a steady rate from a temperature already reached.',
  'l2.ctx.ruleOrchard': 'An orchard yields at a steady rate from a crop already picked.',
  'l2.ctx.ruleRoad': 'A road lengthens at a steady rate from a stretch already laid.',
  'l2.ctx.ruleWater': 'A rain butt fills at a steady rate from a level already in it.',
  'l2.ctx.ruleFleet': 'A fleet grows at a steady rate from a count already in dock.',
  'l2.ctx.ruleMast': 'A mast rises at a steady rate from a section already stepped.',
  'l2.ctx.ruleArchive': 'An archive grows at a steady rate from a shelf already filled.',
  'l2.ctx.ruleGlass': 'A glasshouse warms at a steady rate from a temperature already held.',
  'l2.ctx.ruleWall': 'A sea wall rises at a steady rate from a course already laid.',
  'l2.ctx.ruleFund': 'A fund grows at a steady rate from a sum already banked.',

  // --- the paired-hold deck
  'l2.ctx.holdPallets': 'Two pallet stacks on this dock weigh the same.',
  'l2.ctx.holdCrates': 'Two crate rows in this shed hold the same count.',
  'l2.ctx.holdTanks': 'Two tanks on this rig hold the same volume.',
  'l2.ctx.holdBays': 'Two loading bays take the same mass every watch.',
  'l2.ctx.holdRacks': 'Two core racks in this lab hold the same count.',
  'l2.ctx.holdSpools': 'Two cable spools in this store carry the same length.',
  'l2.ctx.holdKilns': 'Two kilns in this yard fire the same load.',
  'l2.ctx.holdBins': 'Two grain bins on this farm hold the same mass.',
  'l2.ctx.holdWings': 'Two wings of this station house the same crew.',
  'l2.ctx.holdSleds': 'Two sleds on this run tow the same mass.',
  'l2.ctx.holdVats': 'Two dye vats in this works hold the same volume.',
  'l2.ctx.holdBeds': 'Two seed beds in this nursery take the same count.',
  'l2.ctx.holdBanks': 'Two cell banks on this array give the same power.',
  'l2.ctx.holdHoppers': 'Two ore hoppers at this head hold the same mass.',
  'l2.ctx.holdChests': 'Two med chests on this ship hold the same count.',
  'l2.ctx.holdRolls': 'Two canvas rolls in this loft run the same length.',
  'l2.ctx.holdCarts': 'Two spoil carts on this line carry the same mass.',
  'l2.ctx.holdWells': 'Two wells on this site give the same volume.',
  'l2.ctx.holdRuns': 'Two survey runs this week cover the same distance.',
  'l2.ctx.holdLofts': 'Two grain lofts in this tower hold the same mass.',

  // --- the split deck — spare stock added, then the lot divided
  'l2.ctx.shareRun': 'A crew adds spare stock to a run, then splits the lot into equal loads.',
  'l2.ctx.shareCable': 'A rigger adds spare cable to a coil, then cuts it into equal lengths.',
  'l2.ctx.shareWater': 'A cook adds spare water to a tank, then draws it in equal cans.',
  'l2.ctx.shareOre': 'A miner adds spare ore to a heap, then loads it into equal skips.',
  'l2.ctx.shareSeed': 'A grower adds spare seed to a sack, then sows it in equal beds.',
  'l2.ctx.shareFuel': 'An engineer adds spare fuel to a drum, then feeds it in equal doses.',
  'l2.ctx.shareRope': 'A bosun adds spare rope to a bundle, then cuts it into equal falls.',
  'l2.ctx.shareGrain': 'A miller adds spare grain to a bin, then bags it in equal sacks.',
  'l2.ctx.shareSalt': 'A cook adds spare salt to a crib, then packs it in equal blocks.',
  'l2.ctx.shareWax': 'A chandler adds spare wax to a pot, then pours it into equal candles.',
  'l2.ctx.shareCanvas': 'A sailmaker adds spare canvas to a roll, then cuts it into equal panels.',
  'l2.ctx.shareDose': 'A medic adds spare stock to a chest, then splits it into equal doses.',
  'l2.ctx.shareCharge': 'An engineer adds spare charge to a bank, then draws it in equal pulls.',
  'l2.ctx.sharePaint': 'A painter adds spare paint to a drum, then pours it into equal tins.',
  'l2.ctx.shareIce': 'A cutter adds spare ice to a store, then splits it into equal blocks.',
  'l2.ctx.shareTape': 'A fitter adds spare tape to a reel, then cuts it into equal strips.',
  'l2.ctx.shareClay': 'A potter adds spare clay to a batch, then splits it into equal balls.',
  'l2.ctx.shareTimber': 'A carpenter adds spare timber to a pile, then cuts it into equal beams.',
  'l2.ctx.shareThread': 'A weaver adds spare thread to a hank, then winds it onto equal bobbins.',
  'l2.ctx.shareSand': 'A moulder adds spare sand to a heap, then fills equal moulds with it.',

  // --- the log deck
  'l2.ctx.gaugeLog': 'This watch log follows a rule that nobody wrote down.',
  'l2.ctx.gaugeDial': 'This dial follows a rule that nobody wrote down.',
  'l2.ctx.gaugeTide': 'This tide board follows a rule that nobody wrote down.',
  'l2.ctx.gaugeFuel': 'This fuel meter follows a rule that nobody wrote down.',
  'l2.ctx.gaugeDepth': 'This depth sounder follows a rule that nobody wrote down.',
  'l2.ctx.gaugeHeat': 'This heat log follows a rule that nobody wrote down.',
  'l2.ctx.gaugeYield': 'This yield sheet follows a rule that nobody wrote down.',
  'l2.ctx.gaugeSalt': 'This salt reading follows a rule that nobody wrote down.',
  'l2.ctx.gaugeWind': 'This wind record follows a rule that nobody wrote down.',
  'l2.ctx.gaugeSpin': 'This rotor counter follows a rule that nobody wrote down.',
  'l2.ctx.gaugeDose': 'This dose chart follows a rule that nobody wrote down.',
  'l2.ctx.gaugeLoad': 'This load sheet follows a rule that nobody wrote down.',
  'l2.ctx.gaugePower': 'This power log follows a rule that nobody wrote down.',
  'l2.ctx.gaugeFrost': 'This frost record follows a rule that nobody wrote down.',
  'l2.ctx.gaugeSpool': 'This spool counter follows a rule that nobody wrote down.',
  'l2.ctx.gaugeStock': 'This stock ledger follows a rule that nobody wrote down.',
  'l2.ctx.gaugeSignal': 'This signal log follows a rule that nobody wrote down.',
  'l2.ctx.gaugeGrain': 'This grain tally follows a rule that nobody wrote down.',
  'l2.ctx.gaugeAir': 'This air meter follows a rule that nobody wrote down.',
  'l2.ctx.gaugeSpeed': 'This speed trace follows a rule that nobody wrote down.',
  'l2.ctx.gaugeIce': 'This ice core sheet follows a rule that nobody wrote down.',
  'l2.ctx.gaugeWater': 'This water level chart follows a rule that nobody wrote down.',
  'l2.ctx.gaugeTally': 'This crew tally follows a rule that nobody wrote down.',
  'l2.ctx.gaugeRelay': 'This relay count follows a rule that nobody wrote down.',

  // --- the two-condition deck — every sentence binds its letters
  'l2.ctx.pairSacks': 'Here $x$ counts the full sacks and $y$ counts the part sacks.',
  'l2.ctx.pairLamps': 'Here $x$ counts the deck lamps and $y$ counts the hold lamps.',
  'l2.ctx.pairPanes': 'Here $x$ counts the clear panes and $y$ counts the frosted panes.',
  'l2.ctx.pairSeeds': 'Here $x$ counts the seed trays and $y$ counts the cutting trays.',
  'l2.ctx.pairDrums': 'Here $x$ counts the fuel drums and $y$ counts the water drums.',
  'l2.ctx.pairPlates': 'Here $x$ counts the hull plates and $y$ counts the deck plates.',
  'l2.ctx.pairCores': 'Here $x$ counts the ice cores and $y$ counts the rock cores.',
  'l2.ctx.pairSleds': 'Here $x$ counts the light sleds and $y$ counts the heavy sleds.',
  'l2.ctx.pairSpools': 'Here $x$ counts the short spools and $y$ counts the long spools.',
  'l2.ctx.pairCans': 'Here $x$ counts the water cans and $y$ counts the oil cans.',
  'l2.ctx.pairBricks': 'Here $x$ counts the fired bricks and $y$ counts the raw bricks.',
  'l2.ctx.pairFlares': 'Here $x$ counts the day flares and $y$ counts the night flares.',
  'l2.ctx.pairRolls': 'Here $x$ counts the canvas rolls and $y$ counts the net rolls.',
  'l2.ctx.pairCells': 'Here $x$ counts the new cells and $y$ counts the spent cells.',
  'l2.ctx.pairDoses': 'Here $x$ counts the warm doses and $y$ counts the cold doses.',
  'l2.ctx.pairBuoys': 'Here $x$ counts the lit buoys and $y$ counts the dark buoys.',
  'l2.ctx.pairStakes': 'Here $x$ counts the short stakes and $y$ counts the long stakes.',
  'l2.ctx.pairKilns': 'Here $x$ counts the hot kilns and $y$ counts the cold kilns.',
  'l2.ctx.pairSpars': 'Here $x$ counts the wooden spars and $y$ counts the metal spars.',
  'l2.ctx.pairPumps': 'Here $x$ counts the hand pumps and $y$ counts the powered pumps.',


  // --- ten more formulas, so a band deals from four situations and not two
  'l2.ctx.fSquare': 'A hatch cover is a square, and this rule gives the length round it.',
  'l2.ctx.fDensity': 'A block has a fixed density, and this rule gives its mass.',
  'l2.ctx.fCost': 'A store sells parts at one price, and this rule gives the bill.',
  'l2.ctx.fBox': 'A cargo crate is a box, and this rule gives its volume.',
  'l2.ctx.fTriFrame': 'A batten runs round a triangle, and this rule gives its length.',
  'l2.ctx.fSpeedUp': 'A sled speeds up at a steady rate, and this rule gives its speed.',
  'l2.ctx.fTrapezoid': 'A hull panel is a trapezium, and this rule gives its area.',
  'l2.ctx.fThreeMean': 'This rule averages three readings and gives the middle value.',
  'l2.ctx.fBalance': 'A credit balance earns at a fixed rate, and this rule gives the total.',
  'l2.ctx.fLadderSum': 'A ladder of readings climbs by the same step, and this rule gives the total.',

  // --- the dispute deck: both readings printed, so the question can point at them
  'l2.ctx.dispChalk': 'One cadet chalks ${a}$. Another chalks ${b}$ under it.',
  'l2.ctx.dispLog': 'The log carries ${a}$ in one hand and ${b}$ in another.',
  'l2.ctx.dispRadio': 'One console sends ${a}$. The other sends ${b}$.',
  'l2.ctx.dispSlate': 'The slate holds ${a}$, and someone rubbed in ${b}$ beside it.',
  'l2.ctx.dispWatch': 'The morning watch wrote ${a}$. The night watch wrote ${b}$.',
  'l2.ctx.dispQuarrel': 'Two cadets cannot both be right. One wrote ${a}$, one wrote ${b}$.',
  'l2.ctx.dispAudit': 'The audit takes one reading: ${a}$ or ${b}$.',
  'l2.ctx.dispWager': 'One cadet wrote ${a}$. Another wrote ${b}$, and galley duty on it.',
  'l2.ctx.dispBridge': 'The bridge has ${a}$ and ${b}$, and wants one reading.',
  'l2.ctx.dispPost': 'The board shows ${a}$ pinned over ${b}$.',
  'l2.ctx.dispDrill': 'Two cadets ran the same drill. One got ${a}$, one got ${b}$.',
  'l2.ctx.dispInk': 'The sheet carries ${a}$ and ${b}$ in the same ink.',
};
